import { Player as PlayerCore, Events, Environment, Styler, Utils } from '@player/core'
import { Plugins, Playbacks, Errors, playerStyle, mergeMediaControlPlugins, DrawerBottom, MediaControlPlugin } from '@player/plugins'
import { Browser, Events as ClapprEvents, Loader, PlayerError, version as ClapprVersion } from '@clappr/core'

import ShakaPlayer from 'shaka-player'
import HLSjs from 'hls.js'

import { DEFAULT_OPTIONS } from './default_options'

Object.values(Plugins).forEach(plugin => Loader.registerPlugin(plugin))
Playbacks.forEach(playback => Loader.registerPlayback(playback))

const { DOM: { hasPlayerElement }, session, getExperimentAlternative } = Utils
const PlayerSettings = window.WM && window.WM.Settings || {}
const DOMAINS = PlayerSettings && PlayerSettings.Domains || []

class Player extends PlayerCore {
  get eventsMapping() {
    return Object.assign(super.eventsMapping, {
      onPipAdded: Events.PLAYER_PIP_ADDED,
      onPipDiscarded: Events.PLAYER_PIP_DISCARDED,
      onMasterAdded: Events.PLAYER_MASTER_ADDED,
      onMasterChanged: Events.PLAYER_MASTER_CHANGED,
      onPipToMaster: Events.PLAYER_PIPTOMASTER,
      onMediaControlShow: Events.PLAYER_MEDIACONTROL_SHOW,
      onMediaControlHide: Events.PLAYER_MEDIACONTROL_HIDE,
      onBackButtonClicked: Events.BACK_BUTTON_CLICKED,
      onDrawerShow: Events.DID_SHOW_DRAWER,
      onDrawerHide: Events.DID_HIDE_DRAWER,
      onDrawerShowHint: Events.DID_SHOW_DRAWER_HINT,
      onDrawerHideHint: Events.DID_HIDE_DRAWER_HINT,
      onSubtitleChanged: Events.WM_SUBTITLE_CHANGED,
      onAudioChanged: Events.WM_AUDIO_CHANGED,
      onDidLoadCuepoints: Events.DID_LOAD_CUEPOINTS,
      onDidReachCuepoint: Events.DID_REACH_CUEPOINT,
    })
  }

  get defaultOptions() { return DEFAULT_OPTIONS }

  static get version() { return PLAYER_VERSION }

  static get allVersions() {
    return {
      Player: PLAYER_VERSION,
      PlayerCore: PLAYER_CORE_VERSION,
      PlayerPlugins: PLAYER_PLUGINS_VERSION,
      Clappr: ClapprVersion,
      ShakaPlayer: ShakaPlayer.Player.version,
      HLSjs: HLSjs.version,
    }
  }

  get container() { return this.core && this.core.activeContainer }

  get playback() { return this.core && this.core.activePlayback }

  constructor(externalOptions = {}) {
    const mappedSource = Player.hasSourceMapped(externalOptions.source)
    mappedSource && (externalOptions.source = mappedSource)

    let playerOptions = { ...DEFAULT_OPTIONS, ...externalOptions } // eslint-disable-line
    playerOptions.clapprStats = { runEach: 1000, onReport: metrics => this.onMetricsReceived(metrics) }
    playerOptions.mediacontrol.plugins = mergeMediaControlPlugins(playerOptions.mediaControl && playerOptions.mediaControl.plugins)
    playerOptions.bitrateCappingDisabled = PlayerSettings.bitrateCapping && PlayerSettings.bitrateCapping.disabled || PlayerSettings.bitrateCappingDisabled // eslint-disable-line

    if (playerOptions.stretched) {
      playerOptions.width = playerOptions.stretchWidth || playerOptions.width
      playerOptions.height = playerOptions.stretchHeight || playerOptions.height
    }

    Player.overrideOptions(playerOptions)
    playerOptions.plugins && (playerOptions.plugins.loadExternalPluginsFirst = false)
    !playerOptions.includeResetStyle && delete playerOptions.customStyle.resetStyle
    super(playerOptions)

    if (typeof this.options.defaultAudio === 'string') this.options.defaultAudio = this._forceNewAudioFormat(this.options)

    const alternative = getExperimentAlternative(this.options, 'player-isolated-experiment-02')

    if (Browser.isAndroid && alternative === 'android_dvr') {
      Browser.preventUsingCookies = true
      this.options.overrides = { getPlayerType: () => alternative }
    }

    if (Browser.isiOS && alternative === 'ios_dvr') {
      Browser.preventUsingCookies = true
      this.options.overrides = { getPlayerType: () => alternative }
    }
    const HLSJSAlternativePattern = /hlsjs-live-sync-duration-count-(\d+)/
    const liveSyncDurationCount = alternative.match(HLSJSAlternativePattern) && alternative.match(HLSJSAlternativePattern)[1]
    liveSyncDurationCount && (this.options.HLSJSConfiguration = { liveSyncDurationCount })
  }

  static overrideOptions(newOptions = {}, currentOptions = {}) {
    typeof newOptions.chromeless === 'undefined' && currentOptions.chromeless && (newOptions.chromeless = currentOptions.chromeless)
    newOptions.videoInfo && (newOptions.videoInfo = { ...DEFAULT_OPTIONS.videoInfo, ...newOptions.videoInfo })

    const chromecastConfig = newOptions.chromecast || {}
    const appId = chromecastConfig.appId || DEFAULT_OPTIONS.chromecast.appId

    newOptions.chromecast = {
      enabled: chromecastConfig.enabled && !Browser.isChromecast && !!appId,
      appId,
      enableBingeWatch: chromecastConfig.enableBingeWatch,
    }
  }

  static hasSourceMapped(source) { return Browser.isChrome && PlayerSettings.sourceMapping && PlayerSettings.sourceMapping[source] }

  onMetricsReceived(metrics) { this.container.trigger(Events.WM_STATS_REPORTED, metrics) }

  normalizeSources(options = {}) {
    let sources = options.sources || (options.source === undefined ? [] : [`${options.source}`])
    sources = sources.map(source => `${source || ''}`)
    return sources.length === 0 ? ['no.op'] : sources
  }

  isUnsupportedBrowser() {
    const minimumVersions = { chrome: 41, firefox: 38, ie: 12, msie: 12, safari: 8 }
    return Browser.version < minimumVersions[Browser.name.toLowerCase()]
  }

  isCrawlerBot() {
    const { userAgent } = window.navigator

    const isAppleBot = /Applebot/.test(userAgent)
    const isYandexBot = /YandexBot/.test(userAgent)
    const isGoogleBot = /Googlebot/.test(userAgent)
    const isMicrosoftBot = /BingPreview/.test(userAgent)

    return isAppleBot || isGoogleBot || isYandexBot || isMicrosoftBot
  }

  // This method is used by external apps and we need to keep compatibility
  onError(error = {}) { this.core.createError(error) }

  _onError(error = {}) {
    if (error.level !== PlayerError.Levels.FATAL) return

    const errorWithSessionID = { ...error, videoSessionId: session.getVideoSessionId(this.options.playerId) }

    super._onError(errorWithSessionID)
    this.options.onError && this.options.onError(errorWithSessionID)
    if (this.container) {
      this.container.disableMediaControl()
      this.container.stop()
      this.container.destroy()
    }
  }

  attachTo(element) {
    super.attachTo(element)
    this.validatePlayerEnvironment()
  }

  validatePlayerEnvironment() {
    const triggerPlaybackError = this.playback && typeof this.playback.onError === 'function'
    const shouldTriggerNotAllowedDomainError = !Player.isAllowedDomain(window.location.hostname) && !Player.isDevMode() && triggerPlaybackError
    const shouldTriggerUnsupportedBrowserError = (this.isUnsupportedBrowser() || this.isCrawlerBot()) && triggerPlaybackError

    shouldTriggerNotAllowedDomainError && this.playback.onError(Errors.NOT_ALLOWED_DOMAIN, { useCodePrefix: false })
    shouldTriggerUnsupportedBrowserError && this.playback.onError(Errors.UNSUPPORTED_BROWSER, { useCodePrefix: false })
  }

  load(requestedSource) {
    if (!hasPlayerElement(this.element)) return

    const mappedSource = Player.hasSourceMapped(requestedSource)
    const source = mappedSource || requestedSource

    super.load(source)
    this.validatePlayerEnvironment()
  }

  configure(options) {
    const mappedSource = Player.hasSourceMapped(options.source)
    mappedSource && (options.source = mappedSource)

    if (this.options.stretched && (options.width || options.height)) {
      options.width = this.options.stretchWidth || options.width
      options.height = this.options.stretchHeight || options.height
    }

    if (!this.hasSourceChanged(options)) {
      // same source, remove to prevent double load
      delete options.sources
      delete options.source
    }

    options.env = Environment.get(options.env, options.httpOnly)

    if (typeof options.defaultAudio === 'string') options.defaultAudio = this._forceNewAudioFormat(options)

    Player.overrideOptions(options, this.options)
    options.plugins && (options.plugins.loadExternalPluginsFirst = false)
    super.configure(options)
    this.validatePlayerEnvironment()
  }

  hasSourceChanged(options) {
    const prevSource = this.normalizeSources(this.options)
    const newSource = this.normalizeSources(options)

    return prevSource[0] !== newSource[0]
  }

  _forceNewAudioFormat({ defaultAudio }) {
    return { language: defaultAudio }
  }

  _addEventListeners() {
    super._addEventListeners()
    this.listenTo(this.core, Events.WM_PIP_ADDED, this.onPipAdded)
    this.listenTo(this.core, Events.WM_PIP_DISCARDED, this.onPipDiscarded)
    this.listenTo(this.core, Events.WM_MASTER_ADDED, this.onMasterAdded)
    this.listenTo(this.core, Events.WM_MASTER_CHANGED, this.onMasterChanged)
    this.listenTo(this.core, Events.BACK_BUTTON_CLICKED, this.onBackButtonClicked)
    this.listenTo(this.core.mediaControl, ClapprEvents.MEDIACONTROL_SHOW, () => this.trigger(Events.PLAYER_MEDIACONTROL_SHOW))
    this.listenTo(this.core.mediaControl, ClapprEvents.MEDIACONTROL_HIDE, () => this.trigger(Events.PLAYER_MEDIACONTROL_HIDE))
    this.listenTo(this.core, Events.DID_SHOW_DRAWER, this.onDrawerShow)
    this.listenTo(this.core, Events.DID_HIDE_DRAWER, this.onDrawerHide)
    this.listenTo(this.core, Events.DID_SHOW_DRAWER_HINT, this.onDrawerShowHint)
    this.listenTo(this.core, Events.DID_HIDE_DRAWER_HINT, this.onDrawerHideHint)
  }

  _addContainerEventListeners() {
    super._addContainerEventListeners()
    this.listenTo(this.container, Events.WM_PIPTOMASTER, this.onPipToMaster)
    this.listenTo(this.container, Events.WM_SUBTITLE_CHANGED, this.onSubtitleChanged)
    this.listenTo(this.container, Events.DID_LOAD_CUEPOINTS, this.onDidLoadCuepoints)
    this.listenTo(this.container, Events.DID_REACH_CUEPOINT, this.onDidReachCuepoint)
    this.listenTo(this.container, Events.WM_AUDIO_CHANGED, this.onAudioChanged)
  }

  onDrawerHide() {
    this.trigger(Events.DID_HIDE_DRAWER)
  }

  onDrawerShow() {
    this.trigger(Events.DID_SHOW_DRAWER)
  }

  onDrawerHideHint() {
    this.trigger(Events.DID_HIDE_DRAWER_HINT)
  }

  onDrawerShowHint() {
    this.trigger(Events.DID_SHOW_DRAWER_HINT)
  }

  onDidLoadCuepoints(cuepoints) {
    this.trigger(Events.DID_LOAD_CUEPOINTS, cuepoints)
  }

  onDidReachCuepoint(cuepoint) {
    this.trigger(Events.DID_REACH_CUEPOINT, cuepoint)
  }

  onSubtitleChanged(selected) {
    this.trigger(Events.WM_SUBTITLE_CHANGED, selected)
  }

  onAudioChanged(selected) {
    this.trigger(Events.WM_AUDIO_CHANGED, selected)
  }

  onBackButtonClicked() {
    this.trigger(Events.BACK_BUTTON_CLICKED)
  }

  onPipAdded() {
    this.trigger(Events.PLAYER_PIP_ADDED)
  }

  onPipDiscarded() {
    this.trigger(Events.PLAYER_PIP_DISCARDED)
  }

  onMasterAdded() {
    this.trigger(Events.PLAYER_MASTER_ADDED)
  }

  onMasterChanged() {
    this.trigger(Events.PLAYER_MASTER_CHANGED)
  }

  onPipToMaster() {
    this.trigger(Events.PLAYER_PIPTOMASTER)
  }

  version() {
    return Player.version
  }

  allVersions() {
    return Player.allVersions
  }
}

Browser.supportsFakeLocation = /puffin/i.test(navigator.userAgent) // eslint-disable-line
Browser.supportsTouchEvents = 'ontouchstart' in window || navigator.maxTouchPoints > 0 // eslint-disable-line
Browser.isHybridDevice = Browser.supportsTouchEvents && !Browser.isMobile

Player.isDevMode = () => process.env.NODE_ENV === 'development'
Player.isAllowedDomain = domain => !PlayerSettings.Domains || DOMAINS.find(pattern => pattern.test(domain))

export {
  Player,
  Plugins,
  Playbacks,
  Errors,
  Events,
  Styler,
  Environment,
  MediaControlPlugin,
  DrawerBottom,
  playerStyle,
  mergeMediaControlPlugins,
}

window.Clappr = window.Clappr || Clappr
window.$ = window.$ || $
